/**
 * AdminDashboard
 *
 * Main admin landing page with navigation to admin sections.
 * Displays live stats from various admin APIs.
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Brain,
  Users,
  ClipboardList,
  LayoutDashboard,
  TrendingUp,
  Activity,
  Loader2,
  RefreshCw,
  Database,
} from 'lucide-react';
import {
  useGetBetaUsers,
  useGetFeedback,
  useGetAIActivitySummary,
  useGetWaitlist,
} from '../hooks/useAdminApi';
import { getLastNDays } from '../utils/dateHelpers';

interface AdminCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  badgeColor?: 'blue' | 'green' | 'yellow' | 'red';
}

function AdminCard({ title, description, icon, href, badge, badgeColor = 'blue' }: AdminCardProps): JSX.Element {
  const navigate = useNavigate();

  const badgeColors = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <button
      onClick={() => navigate(href)}
      className="card p-6 text-left hover:shadow-md transition-shadow group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-lg bg-evergreen/10 flex items-center justify-center group-hover:bg-evergreen/20 transition-colors">
          {icon}
        </div>
        {badge && (
          <span className={`text-xs font-medium px-2 py-1 rounded ${badgeColors[badgeColor]}`}>
            {badge}
          </span>
        )}
      </div>
      <h3 className="text-lg font-semibold text-ink mb-2">{title}</h3>
      <p className="text-sm text-muted">{description}</p>
    </button>
  );
}

export function AdminDashboard(): JSX.Element {
  const navigate = useNavigate();

  // API hooks for dashboard stats
  const { execute: fetchUsers, loading: loadingUsers, data: usersData } = useGetBetaUsers();
  const { execute: fetchFeedback, loading: loadingFeedback, data: feedbackData } = useGetFeedback();
  const { execute: fetchAIActivity, loading: loadingAI, data: aiData } = useGetAIActivitySummary();
  const { execute: fetchWaitlist, loading: loadingWaitlist, data: waitlistData } = useGetWaitlist();

  // Fetch all stats on mount
  const fetchAllStats = useCallback(async () => {
    const today = getLastNDays(1); // Get today's date range for AI activity

    await Promise.allSettled([
      fetchUsers({ limit: 1 }), // Just need counts
      fetchFeedback({ limit: 1 }), // Just need counts
      fetchAIActivity(today),
      fetchWaitlist({ limit: 1 }), // Just need counts
    ]);
  }, [fetchUsers, fetchFeedback, fetchAIActivity, fetchWaitlist]);

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  const isLoading = loadingUsers || loadingFeedback || loadingAI || loadingWaitlist;

  // Extract stats from API responses
  const activeUsers = usersData?.counts?.active ?? 0;
  const apiCallsToday = aiData?.totalRequests ?? 0;
  const newFeedback = feedbackData?.counts?.new ?? 0;
  const pendingWaitlist = waitlistData?.counts?.pending ?? 0;

  const adminSections: AdminCardProps[] = [
    {
      title: 'Overview',
      description: 'System health, key metrics, and quick stats',
      icon: <LayoutDashboard className="w-6 h-6 text-evergreen" />,
      href: '/admin/overview',
    },
    {
      title: 'Waitlist',
      description: 'Manage waitlist signups and invitations',
      icon: <ClipboardList className="w-6 h-6 text-evergreen" />,
      href: '/admin/waitlist',
    },
    {
      title: 'Users',
      description: 'View and manage user accounts',
      icon: <Users className="w-6 h-6 text-evergreen" />,
      href: '/admin/users',
    },
    {
      title: 'Feedback',
      description: 'Review and respond to user feedback',
      icon: <MessageSquare className="w-6 h-6 text-evergreen" />,
      href: '/admin/feedback',
    },
    {
      title: 'AI Activity',
      description: 'Monitor AI usage, costs, and performance',
      icon: <Brain className="w-6 h-6 text-evergreen" />,
      href: '/admin/ai-activity',
    },
    {
      title: 'Backfill Search Index',
      description: 'Reindex existing documents into Discovery Engine (TASK-022)',
      icon: <Database className="w-6 h-6 text-evergreen" />,
      href: '/admin/backfill',
    },
  ];

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Dashboard
              </button>
              <div className="h-6 w-px bg-soft-neutral" />
              <h1 className="text-xl font-semibold text-ink">Admin</h1>
            </div>
            <button
              onClick={fetchAllStats}
              disabled={isLoading}
              className="btn-ghost btn-sm flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              {loadingUsers ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted" />
              ) : (
                <p className="text-2xl font-semibold text-ink">{activeUsers}</p>
              )}
              <p className="text-xs text-muted">Active Users</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              {loadingAI ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted" />
              ) : (
                <p className="text-2xl font-semibold text-ink">{apiCallsToday}</p>
              )}
              <p className="text-xs text-muted">API Calls Today</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              {loadingFeedback ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted" />
              ) : (
                <p className="text-2xl font-semibold text-ink">{newFeedback}</p>
              )}
              <p className="text-xs text-muted">New Feedback</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              {loadingWaitlist ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted" />
              ) : (
                <p className="text-2xl font-semibold text-ink">{pendingWaitlist}</p>
              )}
              <p className="text-xs text-muted">Pending Waitlist</p>
            </div>
          </div>
        </div>

        {/* Admin Sections Grid */}
        <h2 className="text-lg font-semibold text-ink mb-4">Admin Sections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {adminSections.map((section) => (
            <AdminCard key={section.title} {...section} />
          ))}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
